import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Building2, Mail, Phone, Calendar, DollarSign, Server, Edit2, X, LogIn, MoreVertical, ShieldAlert, CheckCircle, Ban } from 'lucide-react';
import { useAppContext, Customer } from '../../context/AppContext';
export function CustomerManager() {
  const {
    customers,
    updateCustomer
  } = useAppContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'cancelled'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.email.toLowerCase().includes(searchQuery.toLowerCase()) || c.company && c.company.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };
  const handleSignInAs = (customer: Customer) => {
    // In a real app, this would set an impersonation token
    // For this demo, we just route them to the appropriate dashboard
    if (customer.type === 'business') {
      navigate('/dashboard/business');
    } else {
      navigate('/dashboard/customer');
    }
  };
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingCustomer) return;
    const formData = new FormData(e.currentTarget);
    updateCustomer(editingCustomer.id, {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      company: formData.get('company') as string,
      status: formData.get('status') as Customer['status'],
      notes: formData.get('notes') as string
    });
    setIsModalOpen(false);
    setEditingCustomer(null);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'suspended':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };
  return <div className="space-y-8" data-id="element-271">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-272">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-273">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-274">
            Customer Management
          </h1>
          <p className="text-slate-400" data-id="element-275">
            View and manage signed-up customers, their services, and account
            status.
          </p>
        </motion.div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-276">
        <div className="relative flex-1" data-id="element-277">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-278" />
          <input type="text" placeholder="Search customers by name, email, or company..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-279" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-280">
          <option value="all" data-id="element-281">All Statuses</option>
          <option value="active" data-id="element-282">Active</option>
          <option value="suspended" data-id="element-283">Suspended</option>
          <option value="cancelled" data-id="element-284">Cancelled</option>
        </select>
      </div>

      {/* Customer List */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-285">
        <div className="overflow-x-auto" data-id="element-286">
          <table className="w-full text-left text-sm" data-id="element-287">
            <thead className="bg-slate-900/50 border-b border-slate-800 text-slate-400" data-id="element-288">
              <tr data-id="element-289">
                <th className="px-6 py-4 font-medium" data-id="element-290">Customer</th>
                <th className="px-6 py-4 font-medium" data-id="element-291">Contact</th>
                <th className="px-6 py-4 font-medium" data-id="element-292">Services</th>
                <th className="px-6 py-4 font-medium" data-id="element-293">Status</th>
                <th className="px-6 py-4 font-medium text-right" data-id="element-294">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50" data-id="element-295">
              {filteredCustomers.map(customer => <tr key={customer.id} className="hover:bg-slate-900/50 transition-colors" data-id="element-296">
                  <td className="px-6 py-4" data-id="element-297">
                    <div className="flex items-center gap-3" data-id="element-298">
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 font-bold border border-slate-700" data-id="element-299">
                        {customer.name.charAt(0)}
                      </div>
                      <div data-id="element-300">
                        <div className="font-bold text-white flex items-center gap-2" data-id="element-301">
                          {customer.name}
                          {customer.type === 'business' && <Building2 size={12} className="text-fox-500" data-id="element-302" />}
                        </div>
                        {customer.company && <div className="text-slate-500 text-xs" data-id="element-303">
                            {customer.company}
                          </div>}
                        <div className="text-slate-500 text-xs mt-0.5" data-id="element-304">
                          Joined{' '}
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-305">
                    <div className="space-y-1" data-id="element-306">
                      <div className="flex items-center gap-2 text-slate-300" data-id="element-307">
                        <Mail size={14} className="text-slate-500" data-id="element-308" />
                        {customer.email}
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 text-xs" data-id="element-309">
                        <Phone size={14} className="text-slate-500" data-id="element-310" />
                        {customer.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-311">
                    <div className="flex items-center gap-2" data-id="element-312">
                      <Server size={16} className="text-fox-500" data-id="element-313" />
                      <span className="text-white font-medium" data-id="element-314">
                        {customer.services.length}
                      </span>
                      <span className="text-slate-500 text-xs" data-id="element-315">active</span>
                    </div>
                    <div className="text-slate-500 text-xs mt-1" data-id="element-316">
                      ${customer.totalSpent.toFixed(2)} total spent
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-317">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${getStatusColor(customer.status)}`} data-id="element-318">
                      {customer.status === 'active' && <CheckCircle size={12} data-id="element-319" />}
                      {customer.status === 'suspended' && <ShieldAlert size={12} data-id="element-320" />}
                      {customer.status === 'cancelled' && <Ban size={12} data-id="element-321" />}
                      {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-322">
                    <div className="flex items-center justify-end gap-2" data-id="element-323">
                      <button onClick={() => handleSignInAs(customer)} className="p-2 text-fox-500 hover:bg-fox-500/10 rounded-lg transition-colors flex items-center gap-2" title="Sign in as customer" data-id="element-324">
                        <LogIn size={16} data-id="element-325" />
                        <span className="text-xs font-medium hidden xl:inline" data-id="element-326">
                          Sign In As
                        </span>
                      </button>
                      <button onClick={() => handleEdit(customer)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Edit customer" data-id="element-327">
                        <Edit2 size={16} data-id="element-328" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {filteredCustomers.length === 0 && <tr data-id="element-329">
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500" data-id="element-330">
                    <Users size={32} className="mx-auto mb-3 text-slate-600" data-id="element-331" />
                    <p data-id="element-332">No customers found matching your criteria.</p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence data-id="element-333">
        {isModalOpen && editingCustomer && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-334">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-335">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-336">
                <div className="flex items-center gap-3" data-id="element-337">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 font-bold border border-slate-700" data-id="element-338">
                    {editingCustomer.name.charAt(0)}
                  </div>
                  <div data-id="element-339">
                    <h2 className="text-xl font-bold text-white" data-id="element-340">
                      Edit Customer
                    </h2>
                    <p className="text-slate-400 text-xs" data-id="element-341">
                      ID: {editingCustomer.id}
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-342">
                  <X size={24} data-id="element-343" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6" data-id="element-344">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-345">
                  <div className="space-y-2" data-id="element-346">
                    <label className="text-sm font-medium text-slate-300" data-id="element-347">
                      Full Name *
                    </label>
                    <input name="name" defaultValue={editingCustomer.name} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-348" />
                  </div>
                  <div className="space-y-2" data-id="element-349">
                    <label className="text-sm font-medium text-slate-300" data-id="element-350">
                      Email Address *
                    </label>
                    <input name="email" type="email" defaultValue={editingCustomer.email} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-351" />
                  </div>
                  <div className="space-y-2" data-id="element-352">
                    <label className="text-sm font-medium text-slate-300" data-id="element-353">
                      Phone Number *
                    </label>
                    <input name="phone" defaultValue={editingCustomer.phone} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-354" />
                  </div>
                  <div className="space-y-2" data-id="element-355">
                    <label className="text-sm font-medium text-slate-300" data-id="element-356">
                      Company Name
                    </label>
                    <input name="company" defaultValue={editingCustomer.company} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="Optional" data-id="element-357" />
                  </div>
                  <div className="space-y-2" data-id="element-358">
                    <label className="text-sm font-medium text-slate-300" data-id="element-359">
                      Account Status
                    </label>
                    <select name="status" defaultValue={editingCustomer.status} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-360">
                      <option value="active" data-id="element-361">Active</option>
                      <option value="suspended" data-id="element-362">Suspended</option>
                      <option value="cancelled" data-id="element-363">Cancelled</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-364">
                    <label className="text-sm font-medium text-slate-300" data-id="element-365">
                      Account Type
                    </label>
                    <input disabled value={editingCustomer.type.charAt(0).toUpperCase() + editingCustomer.type.slice(1)} className="w-full bg-slate-900 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-500 cursor-not-allowed" data-id="element-366" />
                  </div>
                  <div className="space-y-2 md:col-span-2" data-id="element-367">
                    <label className="text-sm font-medium text-slate-300" data-id="element-368">
                      Admin Notes
                    </label>
                    <textarea name="notes" rows={3} defaultValue={editingCustomer.notes} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none resize-none" placeholder="Internal notes about this customer..." data-id="element-369" />
                  </div>
                </div>

                {/* Services Read-Only View */}
                <div className="mt-8" data-id="element-370">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2" data-id="element-371">
                    <Server size={16} className="text-fox-500" data-id="element-372" /> Active
                    Services
                  </h3>
                  <div className="space-y-3" data-id="element-373">
                    {editingCustomer.services.map((service, idx) => <div key={idx} className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex items-center justify-between" data-id="element-374">
                        <div data-id="element-375">
                          <div className="font-medium text-white flex items-center gap-2" data-id="element-376">
                            {service.packageName}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getStatusColor(service.status)}`} data-id="element-377">
                              {service.status}
                            </span>
                          </div>
                          <div className="text-slate-500 text-xs mt-1" data-id="element-378">
                            {service.domain || 'No domain attached'}
                          </div>
                        </div>
                        <div className="text-right" data-id="element-379">
                          <div className="text-white font-medium" data-id="element-380">
                            ${service.price}/
                            {service.billingCycle === 'monthly' ? 'mo' : 'yr'}
                          </div>
                          <div className="text-slate-500 text-xs mt-1" data-id="element-381">
                            Renews{' '}
                            {new Date(service.nextRenewal).toLocaleDateString()}
                          </div>
                        </div>
                      </div>)}
                    {editingCustomer.services.length === 0 && <div className="text-center py-4 text-slate-500 text-sm bg-slate-950 border border-slate-800 rounded-xl" data-id="element-382">
                        No active services
                      </div>}
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-800 flex justify-between items-center" data-id="element-383">
                  <button type="button" onClick={() => handleSignInAs(editingCustomer)} className="text-fox-500 hover:text-fox-400 font-medium text-sm flex items-center gap-2 transition-colors" data-id="element-384">
                    <LogIn size={16} data-id="element-385" /> Sign In As Customer
                  </button>
                  <div className="flex gap-3" data-id="element-386">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-387">
                      Cancel
                    </button>
                    <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-388">
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}